import { format } from 'date-fns';
import {
  AdviseFeatureResponse,
  FeatureInputImported,
  FeatureInputPreview,
  FeatureInputSupplier,
  FeatureResponseState,
  RequiredFeatureCategory
} from '../../../../types/advise';
import { isNotFoundRefusal, refusalMessage } from '../../../../utils/refusalMessage';

export const STATE_LABELS: Record<FeatureResponseState, string> = {
  supported: 'Supported',
  not_supported: 'Not Supported',
  partial: 'Partial – Caveat'
};

export const STATE_MARKS: Record<FeatureResponseState, string> = {
  supported: '✓',
  partial: '!',
  not_supported: '✕'
};
export const AWAITING_MARK = '…';
export const AWAITING_LABEL = 'Awaiting response';

export const SUPPLIER_GONE = 'That supplier is no longer on the shortlist, so the list has been reloaded.';

/** The words for a refused export, preview or import; the API answers 404 once the supplier has left the shortlist. */
export const supplierRefusal = (error: Parameters<typeof refusalMessage>[0], fallback: string): string =>
  isNotFoundRefusal(error) ? SUPPLIER_GONE : refusalMessage(error, fallback);

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

export const responseKey = (supplierId: string, featureKey: string) => `${supplierId}:${featureKey}`;

export const responsesByCell = (responses: AdviseFeatureResponse[]): Map<string, AdviseFeatureResponse> =>
  new Map(responses.map((response) => [responseKey(response.supplier_id, response.feature_key), response]));

export const requiredFeaturesSummary = (categories: RequiredFeatureCategory[]): string => {
  const features = categories.reduce((count, category) => count + category.features.length, 0);
  return `${plural(features, 'required feature', 'required features')} · ${plural(categories.length, 'category', 'categories')}`;
};

const shortDate = (iso: string) => format(new Date(iso), 'MMM d');

export const roundTripLine = (supplier: FeatureInputSupplier): string => {
  if (!supplier.exported_at) return 'No requirement sheet exported yet';
  const exported = `Requirement sheet exported ${shortDate(supplier.exported_at)}`;
  return supplier.imported_at ? `${exported} · response imported ${shortDate(supplier.imported_at)}` : exported;
};

export const previewSummary = (preview: FeatureInputPreview): string => {
  const flagged = preview.rows.filter((row) => row.flagged).length;
  const parsed = `${preview.rows.length} of ${plural(preview.features_in_file, 'feature', 'features')} parsed`;
  return flagged ? `${parsed} · ${flagged} ${flagged === 1 ? 'needs' : 'need'} a quick look before confirming` : parsed;
};

export const unansweredLine = ({ unanswered }: FeatureInputPreview): string | null =>
  unanswered
    ? `${plural(unanswered, 'feature has', 'features have')} no answer in this file and will read ` +
      `${AWAITING_LABEL} after the import.`
    : null;

export const importedSummary = (imported: FeatureInputImported): string => {
  const updated = `${plural(imported.imported, 'feature', 'features')} updated in the Functionality Matrix.`;
  if (imported.flagged.length === 0) return updated;
  const verb = imported.flagged.length === 1 ? 'was flagged and stays' : 'were flagged and stay';
  return `${updated} ${imported.flagged.length} (${imported.flagged.join(', ')}) ${verb} excluded from Supplier Scoring until you review ${imported.flagged.length === 1 ? 'it' : 'them'}.`;
};
